import React from 'react';
import classNames from 'classnames';

import {TransactionalCTAButton} from '@/features/course-landing-page/components/transactional-cta-button/transactional-cta-button.react-component';
import {CourseIncludes} from '@/features/course-landing-page/components/course-includes/course-includes.react-component';
import {AsyncStaticPriceText} from '@udemy-v2/react-course-pricing';
import {useClpCourse} from '@/features/course-landing-page/context/clp-course-provider.react-component';
import {useClpPurchaseContext} from '@/features/course-landing-page/context/clp-purchase-context.react-component';
import {
    useConsumerSubscriptionGwGPlan,
    useConsumerSubscriptionGwGPlusPersonalPlan,
} from '@udemy-v2/subscription-graphql';
import {GwgEnrollmentCTA} from '@udemy-v2/career-accelerators';
import {useI18n} from '@udemy-v2/i18n';
import {useCourseCareerAccelerator} from '@/features/course-landing-page/context/career-accelerator-provider.react-component';
import {CourseContentPlacement} from '../../course-landing-page.react-component';
import styles from './enrollment-box.module.scss';

interface EnrollmentBoxProps {
    placement: CourseContentPlacement;
}

/**
 * EnrollmentBox component displays the enrollment CTA for courses in a user's subscription or free courses.
 * Note: If a PP user has is marked as enrolled in the course, we'll still show this CTA to them since there's no difference
 * in the user experience between the first and subsequent visits to CLP for PP users.
 */
export const EnrollmentBox = ({placement}: EnrollmentBoxProps) => {
    const isSidebarPlacement = CourseContentPlacement.SIDEBAR === placement;
    const course = useClpCourse();
    const {gettext} = useI18n();
    // Hide price if course is included in subscription, show 'Free' if course is free
    const {isCourseIncludedInUserSubscription} = useClpPurchaseContext();

    // Check if user is subscribed to Grow With Google plans
    const gwgPlan = useConsumerSubscriptionGwGPlan();
    const gwgPlusPersonalPlan = useConsumerSubscriptionGwGPlusPersonalPlan();
    const courseCareerAccelerator = useCourseCareerAccelerator();

    const isSubscribedToGwG =
        (gwgPlan && 'isSubscriber' in gwgPlan && gwgPlan.isSubscriber) ||
        (gwgPlusPersonalPlan &&
            'isSubscriber' in gwgPlusPersonalPlan &&
            gwgPlusPersonalPlan.isSubscriber);

    // Show GwgEnrollmentCTA if user is subscribed to GwG and course has a career accelerator and is not already enrolled
    const shouldShowGwgEnrollmentCTA =
        !course.isEnrolled && isSubscribedToGwG && courseCareerAccelerator?.id;

    return (
        <div
            className={classNames(styles.container, {
                [styles['container--with-margin']]: !isSidebarPlacement,
            })}
            data-purpose="enrollment-box"
        >
            {!isCourseIncludedInUserSubscription && (
                <AsyncStaticPriceText
                    listPriceClassName="ud-heading-lg"
                    courseId={Number(course.id)}
                />
            )}
            {shouldShowGwgEnrollmentCTA ? (
                <GwgEnrollmentCTA
                    udStyle="primary"
                    acceleratorId={Number(courseCareerAccelerator.id)}
                    unEnrolledSubtitle={gettext(
                        'You will be enrolled in all courses included in this path.',
                    )}
                />
            ) : (
                <TransactionalCTAButton udStyle="primary" />
            )}
            {isSidebarPlacement && <CourseIncludes />}
        </div>
    );
};
